import { CommonDialogV2 } from '@ifca-root/react-component/src/components/Dialog/CommonDialogV2';
import {
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
} from '@material-ui/core';
import { PatronPaymentOptions } from 'helpers/patronPaymentOptions';
import { amtStr } from 'helpers/numFormatter';
import React, { useEffect, useState } from 'react';

const PAY_AT_COUNTER = 'PAY_AT_COUNTER';
const PAY_ONLINE = 'PAY_ONLINE';

interface PatronPaymentModeDialogProps {
	open: boolean;
	onClose: () => void;
	options: PatronPaymentOptions;
	onPayOnline: () => void;
	onPayAtCounter: () => void;
	selectedOrder?: {
		docNo?: string | null;
		table?: {
			prefix?: string | null;
			tableNumber?: string | null;
		} | null;
		outstandingAmount?: number | null;
	} | null;
	total?: number | null;
	currencyCode?: string;
}

/**
 * Patron payment choice — same layout as the original customer Payment dialog,
 * with Pay At Counter and Pay Online only.
 */
export const PatronPaymentModeDialog = ({
	open,
	onClose,
	options,
	onPayOnline,
	onPayAtCounter,
	selectedOrder,
	total,
	currencyCode = '',
}: PatronPaymentModeDialogProps) => {
	const { showPayOnline, showPayAtCounter } = options;
	const hasAnyOption = showPayOnline || showPayAtCounter;
	const [paymentMethod, setPaymentMethod] = useState('');

	useEffect(() => {
		if (!open) {
			setPaymentMethod('');
		}
	}, [open]);

	const orderInfoLine = `${selectedOrder?.table?.prefix ?? ''}${selectedOrder
		?.table?.tableNumber ?? ''} ${selectedOrder?.docNo ?? ''}`.trim();

	const displayTotal = total ?? selectedOrder?.outstandingAmount ?? 0;

	const handleConfirm = () => {
		if (paymentMethod === PAY_AT_COUNTER) {
			onPayAtCounter();
			return;
		}
		if (paymentMethod === PAY_ONLINE) {
			onPayOnline();
		}
	};

	return (
		<CommonDialogV2
			fullWidth
			maxWidth="xs"
			open={open}
			onClose={onClose}
			sections={{
				header: {
					dynamic: (
						<div>
							<div className="dialog-dynamic-content">
								<div className="session">
									<div className="flex session">
										<div
											className="title highlight-text flex-space"
											style={{ color: '#ff9800' }}
										>
											Payment
										</div>
									</div>
								</div>
							</div>
							<div className="infoline-content">
								<div className="xsTitle infoline flex-space">
									{orderInfoLine || '—'}
								</div>
								<div className="xsTitle infoline rightText">
									{`${currencyCode}${amtStr(displayTotal)}`}
								</div>
							</div>
						</div>
					),
				},
				body: () => (
					<>
						<span className="mdDesc" style={{ paddingLeft: '15px' }}>
							Payment Method
						</span>

						{!hasAnyOption && (
							<Typography
								color="error"
								variant="body2"
								style={{ padding: '8px 15px 0' }}
							>
								No payment option is available. Please contact staff.
							</Typography>
						)}

						<RadioGroup
							aria-label="patronPaymentMethod"
							name="patronPaymentMethod"
							value={paymentMethod}
							onChange={e => setPaymentMethod(e.target.value)}
						>
							{showPayAtCounter && (
								<FormControlLabel
									value={PAY_AT_COUNTER}
									control={<Radio color="primary" />}
									label="Pay At Counter"
									style={{ paddingLeft: '15px' }}
								/>
							)}
							{showPayOnline && (
								<FormControlLabel
									value={PAY_ONLINE}
									control={<Radio color="primary" />}
									label="Pay Online"
									style={{ paddingLeft: '15px' }}
								/>
							)}
						</RadioGroup>
					</>
				),
				footer: {
					actions: [
						{
							displayText: 'Cancel',
							props: {
								onClick: onClose,
								variant: 'contained',
								color: 'primary',
							},
						},
						{
							displayText: 'Confirm',
							props: {
								onClick: handleConfirm,
								variant: 'contained',
								color: paymentMethod ? 'primary' : 'inherit',
								disabled: !paymentMethod || !hasAnyOption,
							},
						},
					],
				},
			}}
		/>
	);
};
